/**
 * School Lunchbox Service
 * Handles all lunchbox-related operations including menu management, student orders, and calendar views
 */

import { supabaseClient, supabaseAdmin } from '../../supabase/clients.js';

class LunchboxService {
  constructor() {
    this.client = supabaseClient;
    this.admin = supabaseAdmin;
  }

  /**
   * Get all students for lunchbox ordering
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Array>} Array of student objects
   */
  async getStudents(tenantId = 'tn_01') {
    try {
      // Try to get real student data
      try {
        const { data, error } = await this.client
          .from('saas_students')
          .select(`
            id,
            student_name,
            student_id,
            class_id,
            tenant_id,
            created_at,
            updated_at
          `)
          .eq('tenant_id', tenantId)
          .order('class_id', { ascending: true })
          .order('student_name', { ascending: true });

        if (!error && data && data.length > 0) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_students table not found, generating mock data');
      }

      // Fallback: Generate mock student data
      return this.generateMockStudents();
    } catch (error) {
      console.error('Error fetching students:', error);
      return this.generateMockStudents();
    }
  }

  /**
   * Get lunchbox records for a specific date
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Array>} Array of lunchbox records
   */
  async getLunchboxRecords(date, tenantId = 'tn_01') {
    try {
      // Try to get real lunchbox records
      try {
        const { data, error } = await this.client
          .from('saas_lunchbox_records')
          .select(`
            id,
            date,
            student_id,
            lunchbox_type,
            class_id,
            tenant_id,
            created_at,
            saas_students!inner(
              id,
              student_name,
              student_id,
              class_id
            )
          `)
          .eq('date', date)
          .eq('tenant_id', tenantId)
          .order('class_id', { ascending: true })
          .order('student_id', { ascending: true });

        if (!error && data) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_lunchbox_records table not found, generating mock data');
      }

      // Fallback: Generate mock records for the date
      return this.generateMockRecords(date);
    } catch (error) {
      console.error('Error fetching lunchbox records:', error);
      return this.generateMockRecords(date);
    }
  }

  /**
   * Get lunchbox menu for a specific date
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Object>} Menu object with lunchbox types
   */
  async getLunchboxMenu(date, tenantId = 'tn_01') {
    try {
      // Try to get real menu data
      try {
        const { data, error } = await this.client
          .from('saas_lunchbox_menus')
          .select(`
            id,
            date,
            type_a_menu,
            type_b_menu,
            type_c_menu,
            type_d_menu,
            is_holiday,
            tenant_id,
            created_at
          `)
          .eq('date', date)
          .eq('tenant_id', tenantId)
          .single();

        if (!error && data) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_lunchbox_menus table not found, generating mock data');
      }

      // Fallback: Generate mock menu for the date
      return this.generateMockMenu(date);
    } catch (error) {
      console.error('Error fetching lunchbox menu:', error);
      return this.generateMockMenu(date);
    }
  }

  /**
   * Add a new lunchbox record
   * @param {Object} record - Record object
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Object>} Created record
   */
  async addLunchboxRecord(record, tenantId = 'tn_01') {
    try {
      const recordData = {
        date: record.date,
        student_id: record.studentId,
        lunchbox_type: record.lunchboxType,
        class_id: record.classId,
        tenant_id: tenantId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Try to insert real record
      try {
        const { data, error } = await this.client
          .from('saas_lunchbox_records')
          .insert([recordData])
          .select()
          .single();

        if (!error && data) {
          return data;
        }
      } catch (tableError) {
        console.log('saas_lunchbox_records table not found, using mock storage');
      }

      // Fallback: Store in local storage for demo
      const mockRecord = {
        id: `mock_${Date.now()}`,
        ...recordData,
        saas_students: {
          id: record.studentId,
          student_name: record.studentName,
          student_id: record.studentId,
          class_id: record.classId
        }
      };

      // Store in localStorage for demo purposes
      const existingRecords = JSON.parse(localStorage.getItem('mock_lunchbox_records') || '[]');
      existingRecords.push(mockRecord);
      localStorage.setItem('mock_lunchbox_records', JSON.stringify(existingRecords));

      return mockRecord;
    } catch (error) {
      console.error('Error adding lunchbox record:', error);
      throw error;
    }
  }

  /**
   * Get lunchbox statistics
   * @param {string} tenantId - Tenant identifier
   * @returns {Promise<Object>} Statistics object
   */
  async getLunchboxStats(tenantId = 'tn_01') {
    try {
      const students = await this.getStudents(tenantId);
      const totalStudents = students.length;
      const classCount = [...new Set(students.map(s => s.class_id))].length;

      return {
        totalStudents,
        totalClasses: classCount,
        lunchboxTypes: ['A', 'B', 'C', 'D'],
        classList: [...new Set(students.map(s => s.class_id))].sort()
      };
    } catch (error) {
      console.error('Error fetching lunchbox stats:', error);
      return {
        totalStudents: 0,
        totalClasses: 0,
        lunchboxTypes: ['A', 'B', 'C', 'D'],
        classList: []
      };
    }
  }

  /**
   * Generate mock student data
   * @returns {Array} Array of mock student objects
   */
  generateMockStudents() {
    const students = [];
    const classLevels = [1, 2, 3];
    const sections = ['A', 'B', 'C', 'D'];
    const studentNames = [
      '張小明', '李美華', '王大偉', '陳小芳', '劉志強', '黃淑芬', '林建國', '吳雅婷',
      '鄭志明', '周美玲', '蔡文華', '許志強', '謝淑娟', '羅建民', '葉雅文', '何志豪',
      '高美惠', '梁文傑', '蘇志明', '范淑芬', '江建華', '方雅婷', '石志強', '余美玲',
      '鄧文傑'
    ];

    classLevels.forEach(level => {
      sections.forEach(section => {
        const classId = `${level}${section}`;
        
        // Generate 25 students per class
        for (let i = 1; i <= 25; i++) {
          const nameIndex = (level * 4 + section.charCodeAt(0) - 65 + i - 1) % studentNames.length;
          const studentId = `${classId}-${i.toString().padStart(2, '0')}`;
          
          students.push({
            id: `student_${studentId}`,
            student_name: studentNames[nameIndex],
            student_id: studentId,
            class_id: classId,
            tenant_id: 'tn_01',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        }
      });
    });

    // Add Teachers and Staff
    const teachers = ['王老師', '李老師', '張老師', '陳老師', '劉老師', '黃老師', '林老師', '吳老師'];
    const staff = ['鄭主任', '周秘書', '蔡會計', '許工友', '謝護士', '羅技師', '葉司機', '何保安'];

    teachers.forEach((name, index) => {
      students.push({
        id: `teacher_${index + 1}`,
        student_name: name,
        student_id: `Teachers-${(index + 1).toString().padStart(2, '0')}`,
        class_id: 'Teachers',
        tenant_id: 'tn_01',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
    });

    staff.forEach((name, index) => {
      students.push({
        id: `staff_${index + 1}`,
        student_name: name,
        student_id: `Staff-${(index + 1).toString().padStart(2, '0')}`,
        class_id: 'Staff',
        tenant_id: 'tn_01',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
    });

    return students;
  }

  /**
   * Generate mock lunchbox records for a date
   * @param {string} date - Date in YYYY-MM-DD format
   * @returns {Array} Array of mock records
   */
  generateMockRecords(date) {
    // Check if it's a holiday (weekends)
    const dayOfWeek = new Date(date).getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return [];
    }

    // Get records from localStorage if available
    const storedRecords = JSON.parse(localStorage.getItem('mock_lunchbox_records') || '[]');
    const dateRecords = storedRecords.filter(record => record.date === date);

    // If no stored records, generate some random ones for demo
    if (dateRecords.length === 0) {
      const students = this.generateMockStudents();
      const lunchboxTypes = ['A', 'B', 'C', 'D'];
      const randomRecords = [];

      // Generate 20-30 random records for the date
      const recordCount = Math.floor(Math.random() * 11) + 20;
      const usedStudents = new Set();

      for (let i = 0; i < recordCount; i++) {
        let student;
        do {
          student = students[Math.floor(Math.random() * students.length)];
        } while (usedStudents.has(student.id));
        
        usedStudents.add(student.id);

        const record = {
          id: `mock_${date}_${i}`,
          date: date,
          student_id: student.id,
          lunchbox_type: lunchboxTypes[Math.floor(Math.random() * lunchboxTypes.length)],
          class_id: student.class_id,
          tenant_id: 'tn_01',
          created_at: new Date().toISOString(),
          saas_students: {
            id: student.id,
            student_name: student.student_name,
            student_id: student.student_id,
            class_id: student.class_id
          }
        };

        randomRecords.push(record);
      }

      return randomRecords;
    }

    return dateRecords;
  }

  /**
   * Generate mock menu for a date
   * @param {string} date - Date in YYYY-MM-DD format
   * @returns {Object} Mock menu object
   */
  generateMockMenu(date) {
    const dayOfWeek = new Date(date).getDay();
    const isHoliday = dayOfWeek === 0 || dayOfWeek === 6;

    if (isHoliday) {
      return {
        id: `menu_${date}`,
        date: date,
        type_a_menu: '',
        type_b_menu: '',
        type_c_menu: '',
        type_d_menu: '',
        is_holiday: true,
        tenant_id: 'tn_01',
        created_at: new Date().toISOString()
      };
    }

    const menus = {
      type_a_menu: [
        '白切雞配白飯', '紅燒肉配白飯', '糖醋排骨配白飯', '宮保雞丁配白飯',
        '麻婆豆腐配白飯', '魚香肉絲配白飯', '回鍋肉配白飯', '蒜蓉蒸排骨配白飯'
      ],
      type_b_menu: [
        '意大利麵配沙律', '日式咖喱飯', '韓式拌飯', '泰式炒河粉',
        '西式焗飯', '日式拉麵', '韓式炸雞配飯', '泰式綠咖喱配飯'
      ],
      type_c_menu: [
        '蒸蛋配青菜白飯', '清蒸魚配白飯', '白切雞配青菜', '蒸水蛋配白飯',
        '清湯麵配青菜', '蒸蛋羹配白飯', '白灼菜心配白飯', '蒸蛋配豆腐'
      ],
      type_d_menu: [
        '素菜炒飯', '豆腐配青菜白飯', '素菜包配白飯', '蒸蛋配青菜',
        '素菜湯麵', '豆腐配青菜', '素菜炒麵', '蒸蛋配豆腐青菜'
      ]
    };

    const getRandomMenu = (menuArray) => {
      return menuArray[Math.floor(Math.random() * menuArray.length)];
    };

    return {
      id: `menu_${date}`,
      date: date,
      type_a_menu: getRandomMenu(menus.type_a_menu),
      type_b_menu: getRandomMenu(menus.type_b_menu),
      type_c_menu: getRandomMenu(menus.type_c_menu),
      type_d_menu: getRandomMenu(menus.type_d_menu),
      is_holiday: false,
      tenant_id: 'tn_01',
      created_at: new Date().toISOString()
    };
  }
}

// Create and export service instance
export const lunchboxService = new LunchboxService();
export { LunchboxService };
